<template>
  <teleport v-if="isClient" to="body" :disabled="!appendToBody">
    <transition
      name="dialog-fade"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
      @before-leave="beforeLeave"
    >
      <b-overlay
        v-show="visible"
        custom-mask-event
        :mask="modal"
        :z-index="zIndex"
        :overlay-class="modalClass"
      >
        <div
          role="dialog"
          aria-modal="true"
          :aria-label="title || undefined"
          :aria-labelledby="!title ? titleId : undefined"
          :aria-describedby="bodyId"
          :class="[
            `${ns.namespace.value}-overlay-dialog`,
            ns.is('center', center),
            ns.is('fullscreen', fullscreen),
          ]"
          data-test-name="dialog"
          data-test-variant=""
          data-test-state=""
          @click="overlayEvent.onClick"
          @mousedown="overlayEvent.onMousedown"
          @mouseup="overlayEvent.onMouseup"
        >
          <b-focus-trap
            loop
            :trapped="visible"
            focus-start-el="container"
            @focus-after-trapped="onOpenAutoFocus"
            @focus-after-released="onCloseAutoFocus"
            @release-requested="onCloseRequested"
          >
            <b-dialog-content
              v-if="rendered"
              ref="dialogContentRef"
              v-loading="loading"
              :container-class="
                [containerClass, loading ? 'overflow-hidden' : ''].join(' ')
              "
              :body-class="bodyClass"
              :header-class="headerClass"
              :footer-class="footerClass"
              :center="center"
              :close-icon="closeIcon"
              :draggable="draggable"
              :scrollable="scrollable"
              :scrollable-height="scrollableHeight"
              :scrollable-max-height="scrollableMaxHeight"
              :fullscreen="fullscreen"
              :show-close="showClose"
              :style="style"
              :title="title"
              @close="handleClose"
              @scroll="($event) => emit('scroll', $event)"
            >
              <template #header>
                <slot
                  name="header"
                  :close="handleClose"
                  :title-id="titleId"
                  :title-class="ns.e('title')"
                />
              </template>
              <slot />
              <template v-if="$slots.footer" #footer>
                <slot name="footer" />
              </template>
            </b-dialog-content>
          </b-focus-trap>
        </div>
      </b-overlay>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, provide, ref } from 'vue'
import { useMounted } from '@vueuse/core'
import { BOverlay } from '@bigin/components/overlay'
import { useNamespace, useSameTarget } from '@bigin/hooks'
import { dialogInjectionKey } from '@bigin/tokens'
import BFocusTrap from '@bigin/components/focus-trap'
import BDialogContent from './dialog-content.vue'
import { dialogEmits, dialogProps } from './dialog'
import { useDialog } from './use-dialog'

defineOptions({
  name: 'BDialog',
})

const props = defineProps(dialogProps)
const emit = defineEmits(dialogEmits)
const isClient = useMounted()

const ns = useNamespace('dialog')
const dialogRef = ref<HTMLElement>()
const headerRef = ref<HTMLElement>()
const dialogContentRef = ref()

const {
  visible,
  titleId,
  bodyId,
  style,
  rendered,
  zIndex,
  afterEnter,
  afterLeave,
  beforeLeave,
  handleClose,
  onModalClick,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onCloseRequested,
} = useDialog(props, dialogRef)

provide(dialogInjectionKey, {
  dialogRef,
  headerRef,
  bodyId,
  ns,
  rendered,
  style,
})

const overlayEvent = useSameTarget(onModalClick)

const draggable = computed(() => props.draggable && !props.fullscreen)

defineExpose({
  /** @description whether the dialog is visible */
  visible,
  dialogContentRef,
})
</script>
